import {
  AutomationConfiguration,
  AutomationConfigurationPayload,
  AutomationHistoryParams,
  automationService,
} from '@/services/automation.service';
import {
  AutomationConfig,
  AutomationExecution,
  FolderAutomations,
} from '@/features/domains/types/automation.types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

// Query Keys
export const automationKeys = {
  all: ['automations'] as const,
  folder: (folderUuid: string) =>
    ['automations', 'folder', folderUuid] as const,
  folderConfigs: (folderUuid: string) =>
    [...automationKeys.folder(folderUuid), 'configs'] as const,
  folderHistory: (folderUuid: string, params: AutomationHistoryParams) =>
    [...automationKeys.folder(folderUuid), 'history', params] as const,
  execution: (executionUuid: string) =>
    ['automations', 'execution', executionUuid] as const,
};

// Hook to get automation configurations for a folder
export const useFolderAutomations = (folderUuid: string) => {
  return useQuery({
    queryKey: automationKeys.folderConfigs(folderUuid),
    queryFn: () => automationService.getFolderAutomations(folderUuid),
    enabled: !!folderUuid,
    staleTime: 0, // Always fetch fresh data when component mounts
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
  });
};

// Hook to save automation configurations
export const useSaveFolderAutomations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      configurations,
    }: {
      folderUuid: string;
      configurations: AutomationConfigurationPayload[];
    }) => {
      return automationService.saveFolderAutomations(
        folderUuid,
        configurations
      );
    },
    onSuccess: (_data, { folderUuid }) => {
      // Invalidate all queries related to this folder's automations
      queryClient.invalidateQueries({
        queryKey: automationKeys.folder(folderUuid),
      });

      // Also invalidate the specific config query to force refetch
      queryClient.invalidateQueries({
        queryKey: automationKeys.folderConfigs(folderUuid),
      });

      // Remove the query from cache to force fresh fetch next time
      queryClient.removeQueries({
        queryKey: automationKeys.folderConfigs(folderUuid),
      });

      toast.success('Automation configurations saved successfully');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to save automation configurations';
      toast.error(message);
    },
  });
};

// Hook to get automation execution history for a folder
export const useFolderAutomationHistory = (
  folderUuid: string,
  params: AutomationHistoryParams = {}
) => {
  return useQuery({
    queryKey: automationKeys.folderHistory(folderUuid, params),
    queryFn: () =>
      automationService.getFolderAutomationHistory(folderUuid, params),
    enabled: !!folderUuid,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter for execution data)
  });
};

// Enhanced hook for automation history with pagination
export const useAutomationHistoryWithPagination = (folderUuid: string) => {
  const [params, setParams] = useState<AutomationHistoryParams>({
    limit: 20,
    offset: 0,
  });
  const [allExecutions, setAllExecutions] = useState<AutomationExecution[]>([]);

  const {
    data: historyData,
    isLoading,
    error,
    refetch,
  } = useFolderAutomationHistory(folderUuid, params);

  // Update accumulated executions when new data arrives
  useEffect(() => {
    if (historyData?.data) {
      if (params.offset === 0) {
        // First page or reset - replace all data
        setAllExecutions(historyData.data);
      } else {
        // Subsequent pages - append data
        setAllExecutions(prev => [...prev, ...historyData.data]);
      }
    }
  }, [historyData?.data, params.offset]);

  const loadMore = useCallback(() => {
    if (historyData?.hasMore && !isLoading) {
      setParams(prev => ({
        ...prev,
        offset: (prev.offset || 0) + (prev.limit || 20),
      }));
    }
  }, [historyData?.hasMore, isLoading]);

  const refreshHistory = useCallback(() => {
    setAllExecutions([]);
    setParams(prev => ({ ...prev, offset: 0 }));
    refetch();
  }, [refetch]);

  const filterByStatus = useCallback(
    (status?: AutomationHistoryParams['status']) => {
      setAllExecutions([]);
      setParams(prev => ({
        ...prev,
        offset: 0,
        status,
      }));
    },
    []
  );

  const filterByTemplate = useCallback((templateId?: string) => {
    setAllExecutions([]);
    setParams(prev => ({
      ...prev,
      offset: 0,
      templateId,
    }));
  }, []);

  return {
    executions: allExecutions,
    total: historyData?.total || 0,
    hasMore: historyData?.hasMore || false,
    isLoading,
    error,
    loadMore,
    refreshHistory,
    filterByStatus,
    filterByTemplate,
    currentParams: params,
  };
};

// Hook to get details of a specific automation execution
export const useAutomationExecution = (executionUuid: string) => {
  return useQuery({
    queryKey: automationKeys.execution(executionUuid),
    queryFn: () => automationService.getAutomationExecution(executionUuid),
    enabled: !!executionUuid,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Hook to retry a failed automation execution
export const useRetryAutomationExecution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (executionUuid: string) => {
      return automationService.retryAutomationExecution(executionUuid);
    },
    onSuccess: (_result, executionUuid) => {
      // Invalidate execution details
      queryClient.invalidateQueries({
        queryKey: automationKeys.execution(executionUuid),
      });

      // Invalidate folder history to show the new execution
      queryClient.invalidateQueries({
        queryKey: ['automations', 'folder'],
      });

      toast.success('La relance a été ajoutée à la file de traitement');
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Impossible de relancer l'automatisation";
      toast.error(message);
    },
  });
};

// Hook to execute an automation configuration immediately
export const useExecuteConfigurationNow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuid,
      configurationUuid,
    }: {
      folderUuid: string;
      configurationUuid: string;
    }) => {
      return automationService.executeConfigurationNow(
        folderUuid,
        configurationUuid
      );
    },
    onSuccess: (result, { folderUuid }) => {
      console.log({ result });
      if (result.success) {
        // Invalidate folder history to show the new execution
        queryClient.invalidateQueries({
          queryKey: automationKeys.folder(folderUuid),
        });

        toast.success(
          result.message || 'Configuration execution queued successfully'
        );
      } else {
        toast.error(
          result.message || 'Failed to queue configuration execution'
        );
      }
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to execute configuration';
      toast.error(message);
    },
  });
};

// Helper hook to convert local AutomationConfig to API format
export const useAutomationConfigConverter = () => {
  const convertToApiFormat = (
    folderUuid: string,
    domainUuid: string,
    configs: FolderAutomations
  ): AutomationConfigurationPayload[] => {
    return Object.values(configs).map(config => {
      // Gestion des bornes dayOfMonth selon le template
      let minDay = 1;
      let maxDay = 28;
      if (config.templateId === 'calculate-dsn') {
        maxDay = 14;
      } else if (config.templateId === 'generate-bulletin') {
        minDay = 16;
        maxDay = 28;
      } else if (config.templateId === 'generate-edition') {
        maxDay = 31;
      } else if (config.templateId === 'demande-variables') {
        // 1 à 28 comme le sélecteur : un jour absent du mois ne partirait jamais.
        maxDay = 28;
      }
      let dayOfMonth = config.dayOfMonth || minDay;
      dayOfMonth = Math.max(minDay, Math.min(dayOfMonth, maxDay));

      const basePayload: AutomationConfigurationPayload = {
        folderUuid: folderUuid,
        domainUuid: domainUuid,
        templateId: config.templateId,
        isActive: true,
        dayOfMonth: dayOfMonth,
        notificationEmail: config.notificationEmail || '',
        email: config.email,
        sendToEmployeePortal:
          config.templateId === 'generate-bulletin'
            ? Boolean(config.sendToEmployeePortal)
            : false,
        sendToManagerPortal:
          config.templateId === 'generate-bulletin'
            ? Boolean(config.sendToManagerPortal)
            : false,
        // New execution time fields with defaults
        executionHour: config.executionHour ?? 10,
        executionMinute: config.executionMinute ?? 0,
        retryHour: config.retryHour ?? 16,
        retryMinute: config.retryMinute ?? 0,
        // DSN specific fields
        generateBulletinOption: config.generateBulletinOption,
        dsnOption: config.dsnOption,
        sendDsnByEmail: config.sendDsnByEmail || false,
        dsnEmail: config.dsnEmail || '',
        customDescription: config.customDescription,
      };

      // Demande de variables : ce que le lien ouvre (trois choix, « les deux »
      // par défaut).
      if (config.templateId === 'demande-variables') {
        return {
          ...basePayload,
          collecteCanal: config.collecteCanal ?? 'les_deux',
        };
      }

      // Extend payload for generate-edition
      if (config.templateId === 'generate-edition') {
        return {
          ...basePayload,
          editionOptionValue: config.editionOptionValue,
          editionOptionName: config.editionOptionName,
          // Period not selected in UI; backend computes based on schedule
          editionEmail: config.editionEmail || '',
          usePreviousMonth: config.usePreviousMonth ?? true,
        };
      }

      return basePayload;
    });
  };

  const convertFromApiFormat = useCallback(
    (configurations: AutomationConfiguration[]): FolderAutomations => {
      const result: FolderAutomations = {};
      console.log({ configurations });

      configurations.forEach(config => {
        // Gestion des bornes dayOfMonth selon le template
        let minDay = 1;
        let maxDay = 28;
        if (config.templateId === 'calculate-dsn') {
          maxDay = 14;
        } else if (config.templateId === 'generate-bulletin') {
          minDay = 16;
          maxDay = 28;
        } else if (config.templateId === 'generate-edition') {
          maxDay = 31;
        }
        let dayOfMonth = config.dayOfMonth || minDay;
        dayOfMonth = Math.max(minDay, Math.min(dayOfMonth, maxDay));

        const baseConfig: AutomationConfig = {
          templateId: config.templateId,
          email: config.email || '',
          dayOfMonth: dayOfMonth,
          notificationEmail: config.notificationEmail || '',
          sendToEmployeePortal:
            config.templateId === 'generate-bulletin'
              ? Boolean(config.sendToEmployeePortal)
              : false,
          sendToManagerPortal:
            config.templateId === 'generate-bulletin'
              ? Boolean(config.sendToManagerPortal)
              : false,
          // New execution time fields
          executionHour: config.executionHour ?? 10,
          executionMinute: config.executionMinute ?? 0,
          retryHour: config.retryHour ?? 16,
          retryMinute: config.retryMinute ?? 0,
          // DSN specific fields
          dsnOption: config.dsnOption || 'calculate-only',
          sendDsnByEmail: config.sendDsnByEmail || false,
          dsnEmail: config.dsnEmail || '',
          generateBulletinOption:
            config.generateBulletinOption || 'generate-only',
          customDescription: config.customDescription || '',
          collecteCanal: config.collecteCanal ?? 'les_deux',
        };

        // Map edition-specific fields
        if (config.templateId === 'generate-edition') {
          baseConfig.editionOptionValue = config.editionOptionValue || '';
          baseConfig.editionOptionName = config.editionOptionName || '';
          baseConfig.editionEmail = config.editionEmail || '';
          baseConfig.usePreviousMonth = config.usePreviousMonth ?? true;
          result[config.templateId + '-' + config.editionOptionValue] =
            baseConfig;
        } else {
          result[config.templateId] = baseConfig;
        }
      });

      return result;
    },
    []
  );

  return {
    convertToApiFormat,
    convertFromApiFormat,
  };
};

// Hook for bulk automation update
export const useBulkUpdateAutomations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      folderUuids,
      configurations,
    }: {
      folderUuids: string[];
      configurations: AutomationConfigurationPayload[];
    }) => {
      return automationService.bulkUpdateAutomations(
        folderUuids,
        configurations
      );
    },
    onSuccess: (result, { folderUuids }) => {
      // Invalidate all queries related to these folders' automations
      folderUuids.forEach(folderUuid => {
        queryClient.invalidateQueries({
          queryKey: automationKeys.folder(folderUuid),
        });
        queryClient.invalidateQueries({
          queryKey: automationKeys.folderConfigs(folderUuid),
        });
      });

      // Invalidate global folders queries to refresh the list with updated automation status
      queryClient.invalidateQueries({
        queryKey: ['domains', 'folders', 'global'],
      });

      // Also invalidate individual domain folders queries
      queryClient.invalidateQueries({
        queryKey: ['domains'],
        predicate: query => {
          return (
            query.queryKey.includes('folders') &&
            !query.queryKey.includes('global')
          );
        },
      });

      toast.success(
        result.message ||
          `Automation configurations updated for ${result.foldersUpdated} folders`
      );
    },
    onError: (error: HttpApiError) => {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update automation configurations';
      toast.error(message);
    },
  });
};
